import { Refine, Authenticated } from '@refinedev/core';
import { QueryClient, MutationCache } from '@tanstack/react-query';
import routerProvider, { NavigateToResource } from '@refinedev/react-router';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';

import {
  dataProvider,
  authProvider,
  accessControlProvider,
  notificationProvider,
} from './providers';
import { Layout } from './components/layout';
import { ApiKeysList } from './resources/api-keys';
import { McpAccessPage, McpConsentPage } from './resources/mcp';
import { VoiceProfilesList } from './resources/voice-profiles';
import { LoginPage } from './pages/login';
import { SetPasswordPage } from './pages/set-password';
import { DashboardPage } from './pages/dashboard';
import {
  RolesList,
  RolesCreate,
  RolesEdit,
} from './resources/roles';
import { PermissionsList } from './resources/permissions';
import { UsersList, UsersShow } from './resources/users';
import {
  SubscriptionsList,
  SubscriptionsCreate,
  SubscriptionsShow,
} from './resources/subscriptions';
import {
  KnowledgeDocumentsList,
  KnowledgeDocumentsCreate,
  KnowledgeDocumentsShow,
} from './resources/knowledge-documents';
import {
  AdminUsersList,
  AdminUsersCreate,
  AdminUsersEdit,
} from './resources/admin-users';
import { PaymentsList, PaymentsShow } from './resources/payments';
import { OrdersList, OrdersShow } from './resources/orders';
import {
  BroadcastsList,
  BroadcastsCreate,
  BroadcastsShow,
  BroadcastsEdit,
} from './resources/broadcasts';
import {
  MessageTemplatesList,
  MessageTemplatesEdit,
  MessageTemplatesCreate,
} from './resources/message-templates';
import {
  SupportTicketsList,
  SupportTicketsShow,
} from './resources/support-tickets';
import { BotTokensList } from './resources/bot-tokens';
import {
  CoursesList,
  CoursesForm,
  CoursesShow,
  CoursesModules,
  CourseWizard,
} from './resources/courses';
import { BannersList, BannersForm } from './resources/banners';
import { AnnouncementsList, AnnouncementsForm } from './resources/announcements';
import { MiniBannersList, MiniBannersForm } from './resources/mini-banners';
import {
  TeachersList,
  TeachersForm,
} from './resources/catalog';
import {
  TelegramChannelsList,
  TelegramChannelsForm,
} from './resources/telegram-channels';
import { TelegramBotsStatsList } from './resources/telegram-bots';
import {
  ChannelMembershipsShow,
} from './resources/channel-memberships';
import {
  ChannelPassesList,
  ChannelPassesForm,
  ChannelPassesShow,
  ChannelPassWizard,
} from './resources/channel-passes';
import {
  SubscriptionPlansList,
  SubscriptionPlansForm,
  SubscriptionPlansShow,
  SubscriptionPlanWizard,
} from './resources/subscription-plans';
import { TriggersList, TriggersForm } from './resources/triggers';
import {
  LanguagesList,
  LanguagesCreate,
  LanguagesEdit,
} from './resources/languages';
import { TranslationsList, BotMessagesList } from './resources/translations';
import { AIList } from './resources/ai';
import { AuditLogsList } from './resources/audit-logs';
import { ModerationPage } from './resources/moderation';
import { MessagingPage } from './resources/messaging';
import { PostsList, PostsForm, PostShow } from './resources/posts';
import { ReportsList } from './resources/reports';
import { ViolationsList } from './resources/violations';
import { MediaDemoPage } from './pages/media-demo';
import { ForbiddenPage } from './pages/forbidden';
import { TelegramClientPage } from './features/telegram-client';
import { OrderStatusesList, OrderStatusForm } from './resources/order-statuses';
import {
  UserInvitationsList,
  UserInvitationsImport,
} from './resources/user-invitations';
import { CurrenciesList } from './resources/currencies';
import { TagsList, TagsForm, TagsShow } from './resources/tags';
import { UtmSourcesList, UtmSourcesForm } from './resources/utm-sources';
import { CountriesList, CountriesForm } from './resources/countries';
import { DatabasesList } from './resources/databases';
import { ClubSettingsForm } from './resources/club-settings';
import { LegalPagesList, LegalPagesForm } from './resources/legal-pages';
import { UserEntitlementsPage } from './resources/user-entitlements';
import { HomeworkList } from './resources/homework';
import { BooksList, BooksCreate, BooksEdit } from './resources/books';
import { StaffAnalyticsDashboard } from './resources/staff-analytics';
import { DailyStatsList } from './resources/daily-stats';
import {
  LivestreamsList,
  LivestreamsCreate,
  LivestreamsEdit,
  LivestreamsShow,
  LivestreamChatOverlay,
  StageRoomPage,
} from './resources/livestreams';
import { PromoCodesList, PromoCodesForm } from './resources/promo-codes';
import { DiscountsList, DiscountsForm } from './resources/discounts';

import {
  LeadsList,
  LeadsShow,
  LeadFunnelLeadsPage,
  LeadFunnelsList,
  LeadFunnelsForm,
  LeadFunnelStageForm,
  LeadWebhooksList,
  LeadWebhooksCreate,
  LeadWebhooksShow,
} from './resources/leads';

import { Bot } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { IncomingSupportCallModal } from '@/components/support-chat/IncomingSupportCallModal';
import { WebSocketProvider } from './providers/WebSocketProvider';
import { TenantProvider } from './providers/TenantProvider';

/**
 * Resource definitions for Refine
 * Each resource maps to a backend API endpoint
 */
const resources = [
  {
    name: 'media-demo',
    list: '/media-demo',
    meta: {
      label: 'Медиа Тест',
    },
  },
  {
    name: 'admin-users',
    list: '/admin-users',
    create: '/admin-users/create',
    edit: '/admin-users/edit/:id',
    show: '/admin-users/show/:id',
    meta: {
      label: 'Администраторы',
      canDelete: true,
    },
  },
  {
    name: 'users',
    list: '/users',
    show: '/users/show/:id',
    edit: '/users/edit/:id',
    meta: {
      label: 'Пользователи',
      canDelete: true,
    },
  },
  {
    name: 'roles',
    list: '/roles',
    create: '/roles/create',
    edit: '/roles/edit/:id',
    meta: {
      label: 'Роли',
      canDelete: true,
    },
  },
  {
    name: 'permissions',
    list: '/permissions',
    meta: {
      label: 'Права',
    },
  },
  {
    name: 'subscriptions',
    list: '/subscriptions',
    create: '/subscriptions/create',
    edit: '/subscriptions/edit/:id',
    show: '/subscriptions/show/:id',
    meta: {
      label: 'Владельцы абонементов',
    },
  },
  {
    name: 'payments',
    list: '/payments',
    show: '/payments/show/:id',
    meta: {
      label: 'Платежи',
    },
  },
  {
    name: 'orders',
    list: '/orders',
    show: '/orders/show/:id',
    meta: {
      label: 'Заказы',
    },
  },
  {
    name: 'knowledge-documents',
    list: '/knowledge-documents',
    create: '/knowledge-documents/create',
    show: '/knowledge-documents/show/:id',
    meta: {
      label: 'База знаний',
      canDelete: true,
    },
  },
  {
    name: 'broadcasts',
    list: '/broadcasts',
    create: '/broadcasts/create',
    show: '/broadcasts/show/:id',
    edit: '/broadcasts/edit/:id',
    meta: {
      label: 'Рассылки',
    },
  },
  {
    name: 'message-templates',
    list: '/message-templates',
    create: '/message-templates/create',
    edit: '/message-templates/edit/:id',
    meta: {
      label: 'Шаблоны сообщений',
      canDelete: true,
    },
  },
  {
    name: 'support-tickets',
    list: '/support-tickets',
    show: '/support-tickets/show/:id',
    meta: {
      label: 'Обращения',
    },
  },
  {
    name: 'bot-tokens',
    list: '/bot-tokens',
    meta: {
      label: 'Токены бота',
    },
  },
  {
    name: 'subscription-plans',
    list: '/subscription-plans',
    create: '/subscription-plans/create',
    edit: '/subscription-plans/edit/:id',
    show: '/subscription-plans/show/:id',
    meta: {
      label: 'Абонементы',
      canDelete: true,
    },
  },
  {
    name: 'courses',
    list: '/courses',
    create: '/courses/create',
    edit: '/courses/edit/:id',
    show: '/courses/show/:id',
    meta: {
      label: 'Курсы',
      canDelete: true,
    },
  },
  {
    name: 'books',
    list: '/books',
    create: '/books/create',
    edit: '/books/edit/:id',
    meta: {
      label: 'Книги',
      canDelete: true,
    },
  },
  {
    name: 'channel-passes',
    list: '/channel-passes',
    create: '/channel-passes/create',
    edit: '/channel-passes/edit/:id',
    show: '/channel-passes/show/:id',
    meta: {
      label: 'Подписки в канал',
      canDelete: true,
    },
  },
  {
    name: 'banners',
    list: '/banners',
    create: '/banners/create',
    edit: '/banners/edit/:id',
    meta: {
      label: 'Баннеры',
      canDelete: true,
    },
  },
  {
    name: 'announcements',
    list: '/announcements',
    create: '/announcements/create',
    edit: '/announcements/edit/:id',
    meta: {
      label: 'Объявления',
      canDelete: true,
    },
  },
  {
    name: 'mini-banners',
    list: '/mini-banners',
    create: '/mini-banners/create',
    edit: '/mini-banners/edit/:id',
    meta: {
      label: 'Мини-баннеры',
      canDelete: true,
    },
  },
  {
    name: 'teachers',
    list: '/teachers',
    create: '/teachers/create',
    edit: '/teachers/edit/:id',
    show: '/teachers/show/:id',
    meta: {
      label: 'Учителя',
      canDelete: true,
    },
  },
  {
    name: 'telegram-channels',
    list: '/telegram-channels',
    create: '/telegram-channels/create',
    edit: '/telegram-channels/edit/:id',
    meta: {
      label: 'Telegram каналы',
      canDelete: true,
    },
  },
  {
    name: 'telegram-bots',
    list: '/telegram-bots',
    meta: {
      label: 'Telegram боты',
      icon: <Bot className="h-4 w-4" />,
    },
  },
  {
    name: 'channel-memberships',
    show: '/channel-memberships/show/:id',
    meta: {
      label: 'Членство в каналах',
    },
  },
  {
    name: 'triggers',
    list: '/triggers',
    create: '/triggers/create',
    edit: '/triggers/edit/:id',
    meta: {
      label: 'Триггеры',
      canDelete: true,
    },
  },
  {
    name: 'languages',
    list: '/languages',
    create: '/languages/create',
    edit: '/languages/edit/:id',
    meta: {
      label: 'Языки',
    },
  },
  {
    name: 'translation-keys/ui',
    list: '/translations',
    meta: {
      label: 'Локализация UI',
    },
  },
  {
    name: 'translation-keys/bot',
    list: '/bot-messages',
    meta: {
      label: 'Сообщения бота',
    },
  },
  {
    name: 'ai',
    list: '/ai',
    meta: {
      label: 'AI Менеджер',
    },
  },
  {
    name: 'voice-profiles',
    list: '/voice-profiles',
    meta: {
      label: 'Голоса',
    },
  },
  {
    name: 'integrations',
    list: '/integrations',
    meta: {
      label: 'API ключи',
    },
  },
  // MCP-доступ для ИИ-клиентов (пункт виден по праву mcp.use)
  {
    name: 'mcp',
    list: '/mcp',
    meta: {
      label: 'MCP-доступ',
    },
  },
  {
    name: 'audit-logs',
    list: '/audit-logs',
    meta: {
      label: 'Аудит-логи',
    },
  },
  // Messaging
  {
    name: 'messaging',
    list: '/messaging',
    meta: {
      label: 'Сообщения',
    },
  },
  // Social features
  {
    name: 'moderation',
    list: '/moderation',
    meta: {
      label: 'Модерация',
    },
  },
  {
    name: 'posts',
    list: '/posts',
    create: '/posts/create',
    edit: '/posts/edit/:id',
    show: '/posts/show/:id',
    meta: {
      label: 'Посты',
    },
  },
  {
    name: 'reports',
    list: '/reports',
    meta: {
      label: 'Жалобы',
    },
  },
  {
    name: 'violations',
    list: '/violations',
    meta: {
      label: 'Нарушения',
    },
  },
  // Telegram Client
  {
    name: 'telegram-client',
    list: '/telegram-client',
    meta: {
      label: 'Telegram Чаты',
    },
  },
  // Order Statuses (nested under orders)
  {
    name: 'order-statuses',
    list: '/settings/order-statuses',
    create: '/settings/order-statuses/create',
    edit: '/settings/order-statuses/edit/:id',
    meta: {
      label: 'Статусы заказов',
      parent: 'settings',
    },
  },
  // User Invitations
  {
    name: 'user-invitations',
    list: '/user-invitations',
    meta: {
      label: 'Приглашения',
    },
  },
  // Currencies
  {
    name: 'admin/currencies',
    list: '/currencies',
    meta: {
      label: 'Валюты',
    },
  },
  // Club Settings (singleton)
  {
    name: 'club-settings',
    list: '/club-settings',
    show: '/club-settings',
    meta: {
      label: 'Страница о Клубе',
    },
  },
  // Legal Pages
  {
    name: 'legal-pages',
    list: '/legal-pages',
    create: '/legal-pages/create',
    edit: '/legal-pages/edit/:id',
    meta: {
      label: 'Юридические страницы',
      canDelete: true,
    },
  },
  // User Entitlements
  {
    name: 'user-entitlements',
    list: '/user-entitlements',
    meta: {
      label: 'Доступы пользователей',
    },
  },
  // Homework
  {
    name: 'homework',
    list: '/homework',
    meta: {
      label: 'Домашние задания',
    },
  },
  // Staff Analytics — dashboard с аналитикой по работе админов/менеджеров
  {
    name: 'staff-analytics',
    list: '/staff-analytics',
    meta: {
      label: 'Аналитика сотрудников',
    },
  },
  // Daily Stats
  {
    name: 'daily-stats',
    list: '/daily-stats',
    meta: {
      label: 'Ежедневная статистика',
    },
  },
  // Livestreams
  {
    name: 'livestreams',
    list: '/livestreams',
    create: '/livestreams/create',
    edit: '/livestreams/edit/:id',
    show: '/livestreams/show/:id',
    meta: {
      label: 'Прямые трансляции',
      canDelete: true,
    },
  },
  // Promo Codes
  {
    name: 'promo-codes',
    list: '/promo-codes',
    create: '/promo-codes/create',
    edit: '/promo-codes/edit/:id',
    meta: {
      label: 'Промокоды',
      canDelete: true,
    },
  },
  // Discounts (auto-discounts: те же PromoCode с autoApply=true)
  {
    name: 'discounts',
    list: '/discounts',
    create: '/discounts/create',
    edit: '/discounts/edit/:id',
    meta: {
      label: 'Скидки',
      canDelete: true,
    },
  },
  // Tags
  {
    name: 'tags',
    list: '/tags',
    create: '/tags/create',
    edit: '/tags/edit/:id',
    show: '/tags/show/:id',
    meta: {
      label: 'Теги',
      canDelete: true,
    },
  },
  // UTM Sources
  {
    name: 'utm-sources',
    list: '/settings/utm-sources',
    create: '/settings/utm-sources/create',
    edit: '/settings/utm-sources/edit/:id',
    meta: {
      label: 'UTM источники',
    },
  },
  // Countries (страны для регистрации/профиля)
  {
    name: 'admin/countries',
    list: '/settings/countries',
    create: '/settings/countries/create',
    edit: '/settings/countries/edit/:id',
    meta: {
      label: 'Страны',
    },
  },
  // Databases (тенанты — мультитенантность; пункт виден по праву admin.databases.view)
  {
    name: 'databases',
    list: '/settings/databases',
    meta: {
      label: 'Базы данных',
    },
  },
  // Leads CRM
  {
    name: 'leads',
    list: '/leads',
    show: '/leads/show/:id',
    meta: {
      label: 'Лиды',
    },
  },
  {
    name: 'lead-funnels',
    list: '/leads/funnels',
    create: '/leads/funnels/create',
    edit: '/leads/funnels/edit/:id',
    meta: {
      label: 'Воронки',
    },
  },
  // Lead Webhooks
  {
    name: 'lead-webhooks',
    list: '/leads/webhooks',
    create: '/leads/webhooks/create',
    show: '/leads/webhooks/show/:id',
    meta: {
      label: 'Webhook лидов',
    },
  },
];

function LoginRedirect() {
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const loginPath = location.pathname.startsWith('/login')
    ? '/login'
    : `/login?to=${encodeURIComponent(currentPath)}`;

  return <Navigate to={loginPath} replace />;
}

/**
 * Main App component with Refine configuration
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read-only мутации (POST-поиск): ничего в данных не меняют, поэтому глобальный
 * safety-net invalidateQueries() для них вреден — он гасит ВСЕ queries, включая
 * session-status в открытой модалке поиска (CreateInvitationDialog), из-за чего
 * hasSession мигает в false и модалка схлопывается. Эндпоинты вида `…/search`
 * исключаем из инвалидации.
 */
function isReadOnlyMutationUrl(variables: unknown): boolean {
  if (!isRecord(variables) || typeof variables.url !== 'string') {
    return false;
  }
  return /\/search(\?|$)/.test(variables.url);
}

/**
 * useCustomMutation без resource раньше делал queryClient.invalidateQueries() на ВСЁ.
 * Это рефетчит identity/permissions → <Authenticated> кратковременно isLoading
 * и размонтирует Outlet → локальный стейт экранов (импорт инвайтов: step=result)
 * слетает, toast остаётся. Для известных write-URL инвалидируем точечно.
 */
function invalidateForCustomMutationUrl(url: string): boolean {
  // bulk-импорт / send — только список приглашений, не весь кэш
  if (url.includes('/user-invitations')) {
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey.some(
          (k) => typeof k === 'string' && k.includes('user-invitations'),
        ),
    });
    return true;
  }
  return false;
}

/**
 * Извлечение имени ресурса из переменных Refine-мутации.
 *
 * Refine data-хуки (useCreate/useUpdate/useDelete и их *Many-варианты)
 * передают `resource` в переменных вызова mutate(...). Значение может быть
 * либо строкой (`'courses'`), либо объектом ресурса (`{ name: 'courses' }`)
 * — поддерживаем обе формы. useCustomMutation `resource` НЕ передаёт
 * (его переменные — `{ url, method, values, meta }`), для него вернётся
 * undefined и сработает глобальный safety-net ниже.
 */
function extractMutationResource(variables: unknown): string | undefined {
  if (!isRecord(variables)) {
    return undefined;
  }
  const resource = variables.resource;
  if (typeof resource === 'string') {
    return resource;
  }
  if (isRecord(resource) && typeof resource.name === 'string') {
    return resource.name;
  }
  return undefined;
}

/**
 * Глобальный QueryClient с MutationCache, сужающим инвалидацию по ресурсу.
 *
 * Раньше onSuccess делал голый queryClient.invalidateQueries() — после ЛЮБОЙ
 * мутации инвалидировались ВСЕ queries, что вызывало шквал рефетчей на тяжёлых
 * экранах (dashboard, livestreams) и конфликтовало с точечной WS-инвалидацией
 * на странице трансляции (use-livestream-realtime.ts).
 *
 * Теперь:
 * - Если в переменных мутации есть resource (useCreate/useUpdate/useDelete и
 *   *Many) — инвалидируем только queries этого ресурса. Ключи Refine v5 имеют
 *   форму ['data', dataProviderName, resource, ...] (list) и
 *   ['data', dataProviderName, resource, 'one', id, ...] (detail), поэтому
 *   resource всегда присутствует элементом массива → predicate с includes().
 * - resource === 'livestreams' НЕ трогаем: у страницы трансляции своя точечная
 *   WS-инвалидация по url-фрагментам; глобальное гашение её обнуляло бы и
 *   создавало лишние рефетчи аналитики/зрителей.
 * - Если resource нет (useCustomMutation) — оставляем глобальную инвалидацию
 *   как safety-net. Это покрывает кейс замены изображения курса: useCustomMutation
 *   сам по себе не инвалидирует кэш, иначе list/edit отдавали бы старый
 *   bannerImageUrl до hard refresh.
 */
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: (_data, variables) => {
      if (isReadOnlyMutationUrl(variables)) {
        return;
      }
      const resource = extractMutationResource(variables);
      if (resource) {
        if (resource === 'livestreams') {
          return;
        }
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes(resource),
        });
        return;
      }
      // useCustomMutation: точечная инвалидация по url, без глобального сброса
      if (
        isRecord(variables) &&
        typeof variables.url === 'string' &&
        invalidateForCustomMutationUrl(variables.url)
      ) {
        return;
      }
      // Safety-net для прочих custom (upload картинок и т.п.) — но НЕ identity:
      // predicate отсекает auth/me, чтобы не размонтировать Authenticated.
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (!Array.isArray(query.queryKey)) {
            return true;
          }
          // Refine auth keys: ['auth', ...], getIdentity и getPermissions
          const head = query.queryKey[0];
          if (head === 'auth' || head === 'getIdentity' || head === 'getPermissions') {
            return false;
          }
          return true;
        },
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function BookShowRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/books/edit/${id ?? ''}`} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        accessControlProvider={accessControlProvider}
        notificationProvider={notificationProvider}
        routerProvider={routerProvider}
        resources={resources}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          title: {
            text: 'Masters Club Admin',
          },
          reactQuery: {
            clientConfig: queryClient,
          },
        }}
      >
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />
          <Route
            element={(
              <Authenticated
                key="authenticated-overlay-routes"
                fallback={<LoginRedirect />}
              >
                <TenantProvider>
                  <WebSocketProvider>
                    <Outlet />
                  </WebSocketProvider>
                </TenantProvider>
              </Authenticated>
            )}
          >
            <Route path="/livestreams/chat-overlay/:id" element={<LivestreamChatOverlay />} />
            <Route path="/livestreams/stage/:roomId" element={<StageRoomPage />} />
            {/* OAuth-согласие MCP-клиента: без Layout, но под авторизацией */}
            <Route path="/mcp/consent" element={<McpConsentPage />} />
          </Route>
          {/* Protected routes */}
          <Route
            element={(
              <Authenticated
                key="authenticated-routes"
                fallback={<LoginRedirect />}
              >
                <TenantProvider>
                  <WebSocketProvider>
                    <IncomingSupportCallModal />
                    <Layout>
                      <Outlet />
                    </Layout>
                  </WebSocketProvider>
                </TenantProvider>
              </Authenticated>
            )}
          >
            {/* Dashboard */}
            <Route index element={<DashboardPage />} />
            {/* Admin Users */}
            <Route path="/admin-users">
              <Route index element={<AdminUsersList />} />
              <Route path="create" element={<AdminUsersCreate />} />
              <Route path="edit/:id" element={<AdminUsersEdit />} />
            </Route>
            {/* Users */}
            <Route path="/users">
              <Route index element={<UsersList />} />
              <Route path="show/:id" element={<UsersShow />} />
            </Route>
            {/* Roles */}
            <Route path="/roles">
              <Route index element={<RolesList />} />
              <Route path="create" element={<RolesCreate />} />
              <Route path="edit/:id" element={<RolesEdit />} />
            </Route>
            {/* Permissions */}
            <Route path="/permissions">
              <Route index element={<PermissionsList />} />
            </Route>
            {/* Subscriptions */}
            <Route path="/subscriptions">
              <Route index element={<SubscriptionsList />} />
              <Route path="create" element={<SubscriptionsCreate />} />
              <Route path="show/:id" element={<SubscriptionsShow />} />
            </Route>
            {/* Payments */}
            <Route path="/payments">
              <Route index element={<PaymentsList />} />
              <Route path="show/:id" element={<PaymentsShow />} />
            </Route>
            {/* Orders */}
            <Route path="/orders">
              <Route index element={<OrdersList />} />
              <Route path="show/:id" element={<OrdersShow />} />
              {/* Order Statuses */}
              <Route path="statuses" element={<OrderStatusesList />} />
              <Route path="statuses/create" element={<OrderStatusForm />} />
              <Route path="statuses/edit/:id" element={<OrderStatusForm />} />
            </Route>
            {/* Knowledge Base */}
            <Route path="/knowledge-documents">
              <Route index element={<KnowledgeDocumentsList />} />
              <Route path="create" element={<KnowledgeDocumentsCreate />} />
              <Route path="edit/:id" element={<KnowledgeDocumentsCreate />} />
              <Route path="show/:id" element={<KnowledgeDocumentsShow />} />
            </Route>
            {/* Broadcasts */}
            <Route path="/broadcasts">
              <Route index element={<BroadcastsList />} />
              <Route path="create" element={<BroadcastsCreate />} />
              <Route path="show/:id" element={<BroadcastsShow />} />
              <Route path="edit/:id" element={<BroadcastsEdit />} />
            </Route>
            {/* Message Templates (unified) */}
            <Route path="/message-templates">
              <Route index element={<MessageTemplatesList />} />
              <Route path="create" element={<MessageTemplatesCreate />} />
              <Route path="edit/:id" element={<MessageTemplatesEdit />} />
            </Route>
            {/* Support Tickets */}
            <Route path="/support-tickets">
              <Route index element={<SupportTicketsList />} />
              <Route path="show/:id" element={<SupportTicketsShow />} />
            </Route>
            {/* Bot Tokens */}
            <Route path="/bot-tokens">
              <Route index element={<BotTokensList />} />
            </Route>
            {/* Books */}
            <Route path="/books">
              <Route index element={<BooksList />} />
              <Route path="create" element={<BooksCreate />} />
              <Route path="edit/:id" element={<BooksEdit />} />
              <Route path="show/:id" element={<BookShowRedirect />} />
              {/* Fallback: прямой переход по любому другому сегменту (например slug)
                  не имеет выделенной show-страницы в админке — уводим в список книг,
                  чтобы избежать попадания в catch-all с пустым рендером. */}
              <Route path=":slug" element={<Navigate to="/books" replace />} />
            </Route>
            {/* Courses */}
            <Route path="/courses">
              <Route index element={<CoursesList />} />
              <Route path="create" element={<CourseWizard />} />
              <Route path="edit/:id" element={<CoursesForm />} />
              <Route path="show/:id" element={<CoursesShow />} />
              <Route path=":id/modules" element={<CoursesModules />} />
            </Route>
            {/* Banners */}
            <Route path="/banners">
              <Route index element={<BannersList />} />
              <Route path="create" element={<BannersForm />} />
              <Route path="edit/:id" element={<BannersForm />} />
            </Route>
            {/* Announcements */}
            <Route path="/announcements">
              <Route index element={<AnnouncementsList />} />
              <Route path="create" element={<AnnouncementsForm />} />
              <Route path="edit/:id" element={<AnnouncementsForm />} />
            </Route>
            {/* Mini Banners */}
            <Route path="/mini-banners">
              <Route index element={<MiniBannersList />} />
              <Route path="create" element={<MiniBannersForm />} />
              <Route path="edit/:id" element={<MiniBannersForm />} />
            </Route>
            {/* Teachers */}
            <Route path="/teachers">
              <Route index element={<TeachersList />} />
              <Route path="create" element={<TeachersForm />} />
              <Route path="edit/:id" element={<TeachersForm />} />
            </Route>
            {/* Telegram Channels */}
            <Route path="/telegram-channels">
              <Route index element={<TelegramChannelsList />} />
              <Route path="create" element={<TelegramChannelsForm />} />
              <Route path="edit/:id" element={<TelegramChannelsForm />} />
            </Route>
            {/* Telegram Bots (stats overview only) */}
            <Route path="/telegram-bots">
              <Route index element={<TelegramBotsStatsList />} />
            </Route>
            {/* Channel Memberships (show only — list merged into user-entitlements) */}
            <Route path="/channel-memberships">
              <Route path="show/:id" element={<ChannelMembershipsShow />} />
            </Route>
            {/* Channel Passes */}
            <Route path="/channel-passes">
              <Route index element={<ChannelPassesList />} />
              <Route path="create" element={<ChannelPassWizard />} />
              <Route path="edit/:id" element={<ChannelPassesForm />} />
              <Route path="show/:id" element={<ChannelPassesShow />} />
            </Route>
            {/* Subscription Plans */}
            <Route path="/subscription-plans">
              <Route index element={<SubscriptionPlansList />} />
              <Route path="create" element={<SubscriptionPlanWizard />} />
              <Route path="edit/:id" element={<SubscriptionPlansForm />} />
              <Route path="show/:id" element={<SubscriptionPlansShow />} />
            </Route>
            {/* Triggers */}
            <Route path="/triggers">
              <Route index element={<TriggersList />} />
              <Route path="create" element={<TriggersForm />} />
              <Route path="edit/:id" element={<TriggersForm />} />
            </Route>
            {/* Languages */}
            <Route path="/languages">
              <Route index element={<LanguagesList />} />
              <Route path="create" element={<LanguagesCreate />} />
              <Route path="edit/:id" element={<LanguagesEdit />} />
            </Route>
            {/* Translations */}
            <Route path="/translations" element={<TranslationsList />} />
            <Route path="/bot-messages" element={<BotMessagesList />} />
            {/* Voice profiles for livestream translation */}
            <Route path="/voice-profiles" element={<VoiceProfilesList />} />

            {/* Integrations (API Keys) */}
            <Route path="/integrations" element={<ApiKeysList />} />
            {/* MCP-доступ */}
            <Route path="/mcp" element={<McpAccessPage />} />
            {/* AI Manager */}
            <Route path="/ai" element={<AIList />} />
            {/* Audit Logs */}
            <Route path="/audit-logs" element={<AuditLogsList />} />
            {/* Messaging */}
            <Route path="/messaging" element={<MessagingPage />} />
            {/* Moderation */}
            <Route path="/moderation" element={<ModerationPage />} />
            {/* Posts */}
            <Route path="/posts">
              <Route index element={<PostsList />} />
              <Route path="create" element={<PostsForm />} />
              <Route path="edit/:id" element={<PostsForm />} />
              <Route path="show/:id" element={<PostShow />} />
            </Route>
            {/* Reports */}
            <Route path="/reports" element={<ReportsList />} />
            {/* Violations */}
            <Route path="/violations" element={<ViolationsList />} />
            {/* Telegram Client */}
            <Route path="/telegram-client" element={<TelegramClientPage />} />
            {/* Media Demo */}
            <Route path="/media-demo" element={<MediaDemoPage />} />
            {/* User Invitations */}
            <Route path="/user-invitations">
              <Route index element={<UserInvitationsList />} />
              <Route path="import" element={<UserInvitationsImport />} />
            </Route>
            {/* Currencies */}
            <Route path="/currencies" element={<CurrenciesList />} />
            {/* Homework */}
            <Route path="/homework" element={<HomeworkList />} />
            {/* Legal Pages */}
            <Route path="/legal-pages">
              <Route index element={<LegalPagesList />} />
              <Route path="create" element={<LegalPagesForm />} />
              <Route path="edit/:id" element={<LegalPagesForm />} />
            </Route>
            {/* Club Settings */}
            <Route path="/club-settings" element={<ClubSettingsForm />} />
            {/* User Entitlements (includes channel memberships tab) */}
            <Route path="/user-entitlements" element={<UserEntitlementsPage />} />
            {/* Staff Analytics dashboard */}
            <Route path="/staff-analytics" element={<StaffAnalyticsDashboard />} />
            {/* Daily Stats */}
            <Route path="/daily-stats" element={<DailyStatsList />} />
            {/* Livestreams */}
            <Route path="/livestreams">
              <Route index element={<LivestreamsList />} />
              <Route path="create" element={<LivestreamsCreate />} />
              <Route path="edit/:id" element={<LivestreamsEdit />} />
              <Route path="show/:id" element={<LivestreamsShow />} />
            </Route>
            {/* Promo Codes */}
            <Route path="/promo-codes">
              <Route index element={<PromoCodesList />} />
              <Route path="create" element={<PromoCodesForm />} />
              <Route path="edit/:id" element={<PromoCodesForm />} />
            </Route>
            {/* Discounts (autoApply slice of PromoCode) */}
            <Route path="/discounts">
              <Route index element={<DiscountsList />} />
              <Route path="create" element={<DiscountsForm />} />
              <Route path="edit/:id" element={<DiscountsForm />} />
            </Route>
            {/* Tags */}
            <Route path="/tags">
              <Route index element={<TagsList />} />
              <Route path="create" element={<TagsForm />} />
              <Route path="edit/:id" element={<TagsForm />} />
              <Route path="show/:id" element={<TagsShow />} />
            </Route>
            {/* Settings — UTM Sources */}
            <Route path="/settings/utm-sources">
              <Route index element={<UtmSourcesList />} />
              <Route path="create" element={<UtmSourcesForm />} />
              <Route path="edit/:id" element={<UtmSourcesForm />} />
            </Route>
            {/* Settings — Countries */}
            <Route path="/settings/countries">
              <Route index element={<CountriesList />} />
              <Route path="create" element={<CountriesForm />} />
              <Route path="edit/:id" element={<CountriesForm />} />
            </Route>
            {/* Settings — Databases (тенанты) */}
            <Route path="/settings/databases">
              <Route index element={<DatabasesList />} />
            </Route>
            {/* Leads CRM */}
            <Route path="/leads">
              <Route index element={<LeadsList />} />
              <Route path="show/:id" element={<LeadsShow />} />
              <Route path="funnels">
                <Route index element={<LeadFunnelsList />} />
                <Route path="create" element={<LeadFunnelsForm />} />
                <Route path="edit/:id" element={<LeadFunnelsForm />} />
                <Route path=":funnelId" element={<LeadFunnelLeadsPage />} />
                <Route path=":funnelId/stages/create" element={<LeadFunnelStageForm />} />
                <Route path="stages/edit/:id" element={<LeadFunnelStageForm />} />
              </Route>
              {/* Lead Webhooks */}
              <Route path="webhooks">
                <Route index element={<LeadWebhooksList />} />
                <Route path="create" element={<LeadWebhooksCreate />} />
                <Route path="show/:id" element={<LeadWebhooksShow />} />
              </Route>
            </Route>
            {/* Catch all - redirect to dashboard */}
            <Route
              path="*"
              element={<NavigateToResource resource="dashboard" />}
            />
          </Route>
        </Routes>
      </Refine>

      {/* Toast notifications */}
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}
